import Dexie, { Table } from "dexie";
import {
  createEmptyVault,
  createImportResult,
  FletchyVault,
  normalizeVault,
  parseVault,
  serializeVault,
  StorageDriver,
  VaultData,
} from "@fletchy/core";

interface VaultRecord {
  id: "primary";
  vault: FletchyVault;
  updatedAt: string;
}

class FletchyDatabase extends Dexie {
  vaults!: Table<VaultRecord, string>;

  constructor(name: string) {
    super(name);
    this.version(1).stores({
      vaults: "id,updatedAt",
    });
  }
}

export class DexieStorageDriver implements StorageDriver {
  private readonly db: FletchyDatabase;

  constructor(
    dbName: string,
    private readonly seedFactory: () => FletchyVault = createEmptyVault,
  ) {
    this.db = new FletchyDatabase(dbName);
  }

  async loadVault(): Promise<FletchyVault> {
    const record = await this.db.vaults.get("primary");
    if (record) {
      return normalizeVault(record.vault);
    }

    const vault = this.seedFactory();
    await this.saveVault(vault);
    return vault;
  }

  async saveVault(vault: FletchyVault): Promise<void> {
    const normalized = normalizeVault(vault);
    await this.db.vaults.put({
      id: "primary",
      vault: normalized,
      updatedAt: normalized.updatedAt,
    });
  }

  async exportVault(): Promise<string> {
    const vault = await this.loadVault();
    return serializeVault(vault);
  }

  async importVault(data: VaultData): Promise<ReturnType<typeof createImportResult>> {
    const vault = parseVault(data);
    await this.saveVault(vault);
    return createImportResult(
      {
        notebooks: vault.notebooks.length,
        notes: vault.notes.length,
        cells: vault.cells.length,
      },
      {},
      [],
    );
  }

  async resetVault(seed = this.seedFactory()): Promise<FletchyVault> {
    const normalized = normalizeVault(seed);
    await this.saveVault(normalized);
    return normalized;
  }
}
